import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  IBaseConfigurationFormModel,
  IDatePickerFieldsConfigFormModel,
  IFormbuilderFieldTypeConfiguration,
} from '../../../../core/models/form-builder-types-configurations.model';
import { fieldsBaseConfigurationFormModel } from './shared.configuration';
import {
  baseFieldParseIntoSettingsFormModel,
  baseFieldUpdateFromSettingsFormModel,
  getDateFieldSettingsConfigFormly,
} from '../form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import { FormBuilderService } from '../../../../core/services/form-builder.service';
import {
  getIdConditionsValidatorFormConfig,
  getRequiredValidatorFormConfig,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-generator.util';
import { getFieldDisplayFormConfig } from '../form-fields-formly-display/form-fields-formly-display-field-generator.util';
import { EIremboFormlyFieldTypes } from '@irembo-andela/irembogov3-common';
import {
  parseIdConditionsValidatorParseFieldIntoModel,
  parseRequiredValidatorParseFieldIntoModel,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-parse-to-model.util';
import {
  setIdConditionsValidatorUpdateFieldFromModel,
  setRequiredValidatorUpdateFieldFromModel,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-set-from-model.util';

export const defaultInputConfigFormModel: IDatePickerFieldsConfigFormModel = {
  ...fieldsBaseConfigurationFormModel(),
  fieldLabel: '',
  placeholder: '',
  hint: '',
  tooltip: '',
  internalDescription: '',
  dateFormat: 'DD MM YYYY',
  delimeter: '/',
  eventCode: '',
  eventDatesMaxSize: '',
};

export const getExtendedDateFieldSettingsConfigFormly = (
  formBuilderServiceInstance: FormBuilderService
): FormlyFieldConfig[] => [
  ...getDateFieldSettingsConfigFormly(formBuilderServiceInstance),
];

// Custom function to create date picker specific validation fields
const getDatePickerValidationFieldsFormConfig = (
  formBuilderService: FormBuilderService
): FormlyFieldConfig => {
  // Create a new validation config directly instead of extending the base one
  // to avoid inheriting unwanted field structure
  const validationConfig: FormlyFieldConfig = {
    fieldGroup: [],
  };

  // Additional datepicker-specific validation fields
  const datepickerValidationFields: FormlyFieldConfig[] = [
    // Min date limit configuration
    getRequiredValidatorFormConfig(),
    getIdConditionsValidatorFormConfig(),
    {
      key: 'minDateConfig',
      type: EIremboFormlyFieldTypes['formly-group'],
      wrappers: ['panel-wrapper'],
      props: {
        label: 'Minimum Date',
      },
      fieldGroup: [
        {
          key: 'minDateType',
          type: EIremboFormlyFieldTypes.radio,
          defaultValue: 'none',
          props: {
            label: 'Minimum Date Type',
            options: [
              { label: 'None', value: 'none' },
              { label: 'Relative to Today', value: 'relative' },
            ],
          },
        },
        {
          key: 'relativeMinDateValue',
          type: EIremboFormlyFieldTypes.input,
          props: {
            type: 'number',
            label: 'Number of Time Units',
            placeholder: 'Enter a number',
          },
          expressions: {
            'props.hideField': (field: FormlyFieldConfig) => {
              return field.parent?.model?.['minDateType'] !== 'relative';
            },
            'props.required': (field: FormlyFieldConfig) => {
              return field.parent?.model?.['minDateType'] === 'relative';
            },
          },
        },
        {
          key: 'relativeMinDateUnit',
          type: EIremboFormlyFieldTypes.customdropdown,
          props: {
            label: 'Time Unit',
            options: [
              { label: 'Days', value: 'd' },
              { label: 'Months', value: 'm' },
              { label: 'Years', value: 'y' },
            ],
            bindLabel: 'label',
            bindValue: 'value',
          },
          expressions: {
            'props.hideField': (field: FormlyFieldConfig) => {
              return field.parent?.model?.['minDateType'] !== 'relative';
            },
            'props.required': (field: FormlyFieldConfig) => {
              return field.parent?.model?.['minDateType'] === 'relative';
            },
          },
        },
      ],
    },

    // Max date limit configuration
    {
      key: 'maxDateConfig',
      type: EIremboFormlyFieldTypes['formly-group'],
      wrappers: ['panel-wrapper'],
      props: {
        label: 'Maximum Date',
      },
      fieldGroup: [
        {
          key: 'maxDateType',
          type: EIremboFormlyFieldTypes.radio,
          defaultValue: 'none',
          props: {
            label: 'Maximum Date Type',
            options: [
              { label: 'None', value: 'none' },
              { label: 'Relative to Today', value: 'relative' },
            ],
          },
        },
        {
          key: 'relativeMaxDateValue',
          type: EIremboFormlyFieldTypes.input,
          props: {
            type: 'number',
            label: 'Number of Time Units',
            placeholder: 'Enter a number',
          },
          expressions: {
            'props.hideField': (field: FormlyFieldConfig) => {
              return field.parent?.model?.['maxDateType'] !== 'relative';
            },
            'props.required': (field: FormlyFieldConfig) => {
              return field.parent?.model?.['maxDateType'] === 'relative';
            },
          },
        },
        {
          key: 'relativeMaxDateUnit',
          type: EIremboFormlyFieldTypes.customdropdown,
          props: {
            label: 'Time Unit',
            options: [
              { label: 'Days', value: 'd' },
              { label: 'Months', value: 'm' },
              { label: 'Years', value: 'y' },
            ],
            bindLabel: 'label',
            bindValue: 'value',
          },
          expressions: {
            'props.hideField': (field: FormlyFieldConfig) => {
              return field.parent?.model?.['maxDateType'] !== 'relative';
            },
            'props.required': (field: FormlyFieldConfig) => {
              return field.parent?.model?.['maxDateType'] === 'relative';
            },
          },
        },
      ],
    },

    // Allowed days of week
    {
      key: 'allowedDaysOfTheWeek',
      type: EIremboFormlyFieldTypes.multicheckbox,
      wrappers: ['panel-wrapper'],
      props: {
        label: 'Allowed Days of Week',
        options: [
          { label: 'Sunday', value: 0 },
          { label: 'Monday', value: 1 },
          { label: 'Tuesday', value: 2 },
          { label: 'Wednesday', value: 3 },
          { label: 'Thursday', value: 4 },
          { label: 'Friday', value: 5 },
          { label: 'Saturday', value: 6 },
        ],
        description: 'Select days of the week that are allowed for selection',
      },
    },
  ];

  // Add the datepicker validation fields to the config
  validationConfig.fieldGroup = [...datepickerValidationFields];

  // Add a separate section for error messages
  validationConfig.fieldGroup.push({
    key: 'validationMessages',
    wrappers: ['panel-wrapper'],
    type: EIremboFormlyFieldTypes.block,
    props: {
      label: 'Error Messages',
    },
    fieldGroup: [
      {
        key: 'ngbDate',
        type: EIremboFormlyFieldTypes.input,
        props: {
          label: 'Invalid Date Format:',
          placeholder: 'Enter invalid date format error message',
        },
      },
      {
        key: 'dayOfWeekIsNotAllowed',
        type: EIremboFormlyFieldTypes.input,
        props: {
          label: 'Day of Week Not Allowed:',
          placeholder: 'Enter day of week not allowed error message',
        },
        expressions: {
          'props.hideField': (field: FormlyFieldConfig) => {
            const rootModel = field.options?.formState?.mainModel;
            return (
              !rootModel?.allowedDaysOfTheWeek ||
              Object.keys(rootModel?.allowedDaysOfTheWeek || {}).length === 0
            );
          },
        },
      },
      {
        key: 'unavailableSlots',
        type: EIremboFormlyFieldTypes.input,
        props: {
          label: 'Unavailable Slots:',
          placeholder: 'Enter unavailable slots error message',
        },
        expressions: {
          'props.hideField': (field: FormlyFieldConfig) => {
            const rootModel = field.options?.formState?.mainModel;
            return !rootModel?.eventCode;
          },
        },
      },
    ],
  });

  return validationConfig;
};

export const datePickerFieldGetSettingsConfigurationConfig = (
  formBuilderServiceInstance: FormBuilderService
): IFormbuilderFieldTypeConfiguration => {
  return {
    settings: {
      formModel: defaultInputConfigFormModel,
      formlyForm: [
        ...getExtendedDateFieldSettingsConfigFormly(formBuilderServiceInstance),
      ],
    },
    validations: {
      formModel: {
        required: false,
        minDateConfig: {
          minDateType: 'none',
          relativeMinDateValue: '',
          relativeMinDateUnit: 'd',
        },
        maxDateConfig: {
          maxDateType: 'none',
          relativeMaxDateValue: '',
          relativeMaxDateUnit: 'd',
        },
        allowedDaysOfTheWeek: {},
        validationMessages: {},
      },
      formlyForm: [
        getDatePickerValidationFieldsFormConfig(formBuilderServiceInstance),
      ],
    },
    display: {
      formModel: {},
      formlyForm: [...getFieldDisplayFormConfig(formBuilderServiceInstance)],
    },
  };
};

export const datePickerFieldParseIntoSettingsFormModel = (
  field: FormlyFieldConfig
): IDatePickerFieldsConfigFormModel => {
  const baseSetting: IBaseConfigurationFormModel =
    baseFieldParseIntoSettingsFormModel(field);
  return {
    ...baseSetting,
    dateFormat: field.props?.['dateFormat'],
    delimeter: field.props?.['delimeter'],
    eventCode: field.props?.['eventCode'],
    eventDatesMaxSize: field.props?.['eventDatesMaxSize'],
  };
};

export const datePickerFieldParseIntoValidationFormModel = (
  field: FormlyFieldConfig
): Record<string, any> => {
  const validationModel: Record<string, any> = {
    required: field.props?.required ?? false,
    allowedDaysOfTheWeek:
      convertDaysArrayToObject(field.props?.['allowedDaysOfTheWeek']) ??
      undefined,
    eventCode: field.props?.['eventCode'] ?? '',
    eventDatesMaxSize: field.props?.['eventDatesMaxSize'] ?? '',
    validationMessages: {},
  };

  // Parse min date configuration
  validationModel['minDateConfig'] = {
    minDateType: 'none',
    relativeMinDateValue: '',
    relativeMinDateUnit: 'd',
  };

  if (field.props?.['addRemoveTimeMinDate']) {
    const minDateStr = field.props['addRemoveTimeMinDate'];
    const parts = minDateStr.split(':');
    if (parts.length === 2) {
      validationModel['minDateConfig'].minDateType = 'relative';
      validationModel['minDateConfig'].relativeMinDateValue = parts[0];
      validationModel['minDateConfig'].relativeMinDateUnit = parts[1];
    }
  }

  // Parse max date configuration
  validationModel['maxDateConfig'] = {
    maxDateType: 'none',
    relativeMaxDateValue: '',
    relativeMaxDateUnit: 'd',
  };

  if (field.props?.['addRemoveTimeMaxDate']) {
    const maxDateStr = field.props['addRemoveTimeMaxDate'];
    const parts = maxDateStr.split(':');
    if (parts.length === 2) {
      validationModel['maxDateConfig'].maxDateType = 'relative';
      validationModel['maxDateConfig'].relativeMaxDateValue = parts[0];
      validationModel['maxDateConfig'].relativeMaxDateUnit = parts[1];
    }
  }

  return {
    ...parseIdConditionsValidatorParseFieldIntoModel(
      parseRequiredValidatorParseFieldIntoModel(validationModel, field),
      field
    ),
    ...validationModel,
  };
};

export const datePickerFieldUpdateFromSettingsFormModel = (
  model: IDatePickerFieldsConfigFormModel,
  datePickerField: FormlyFieldConfig
): FormlyFieldConfig => {
  baseFieldUpdateFromSettingsFormModel(model, datePickerField);

  datePickerField = setRequiredValidatorUpdateFieldFromModel(
    model,
    datePickerField
  );
  if (datePickerField.props) {
    datePickerField.props['dateFormat'] = model.dateFormat ?? undefined;
    datePickerField.props['delimeter'] = model.delimeter ?? undefined;
    datePickerField.props['eventCode'] = model.eventCode ?? undefined;
    datePickerField.props['eventDatesMaxSize'] =
      model.eventDatesMaxSize ?? undefined;
  } else {
    datePickerField.props = {
      dateFormat: model.dateFormat,
      delimeter: model.delimeter,
      eventCode: model.eventCode,
      eventDatesMaxSize: model.eventDatesMaxSize,
    };
  }

  return datePickerField;
};

export const datePickerFieldUpdateFromValidationFormModel = (
  model: Record<string, any>,
  datePickerField: FormlyFieldConfig
): FormlyFieldConfig => {
  // Initialize props if needed
  if (!datePickerField.props) {
    datePickerField.props = {};
  }

  // Set the required property
  datePickerField = setRequiredValidatorUpdateFieldFromModel(
    model,
    datePickerField
  );
  datePickerField = setIdConditionsValidatorUpdateFieldFromModel(
    model,
    datePickerField
  );
  const props = datePickerField.props || (datePickerField.props = {});

  // Process allowed days of week - direct copy from model to props
  if (model?.['allowedDaysOfTheWeek']) {
    const daysAllowed = convertDaysObjectToArray(model['allowedDaysOfTheWeek']);
    if (daysAllowed.length) {
      props['allowedDaysOfTheWeek'] = daysAllowed;
    } else {
      delete props['allowedDaysOfTheWeek'];
    }
  }

  // Process min date configuration - convert from UI format to component format
  const minDateConfig = model?.['minDateConfig'];
  if (minDateConfig) {
    if (
      minDateConfig.minDateType === 'relative' &&
      minDateConfig.relativeMinDateValue &&
      minDateConfig.relativeMinDateUnit
    ) {
      const value = minDateConfig.relativeMinDateValue;
      const unit = minDateConfig.relativeMinDateUnit;
      props['addRemoveTimeMinDate'] = `${value}:${unit}`;
    } else {
      props['addRemoveTimeMinDate'] = undefined;
    }
  }

  // Process max date configuration - convert from UI format to component format
  const maxDateConfig = model?.['maxDateConfig'];
  if (maxDateConfig) {
    if (
      maxDateConfig.maxDateType === 'relative' &&
      maxDateConfig.relativeMaxDateValue &&
      maxDateConfig.relativeMaxDateUnit
    ) {
      const value = maxDateConfig.relativeMaxDateValue;
      const unit = maxDateConfig.relativeMaxDateUnit;
      props['addRemoveTimeMaxDate'] = `${value}:${unit}`;
    } else {
      props['addRemoveTimeMaxDate'] = undefined;
    }
  }

  return datePickerField;
};

/**
 * Convert allowedDaysOfTheWeek from object format {0: true, 1: true} to array format [0, 1]
 * @param daysObject Object with day indices as keys and boolean values
 * @returns Array of day indices (0-6)
 */
export function convertDaysObjectToArray(
  daysObject: Record<string, boolean> | undefined
): number[] {
  if (!daysObject) return [];

  return Object.entries(daysObject)
    .filter(([_, isSelected]) => isSelected)
    .map(([day, _]) => parseInt(day, 10));
}

/**
 * Convert allowedDaysOfTheWeek from array format [0, 1] to object format {0: true, 1: true}
 * @param daysArray Array of day indices (0-6)
 * @returns Object with day indices as keys and boolean values
 */
function convertDaysArrayToObject(
  daysArray: number[] | undefined
): Record<string, boolean> {
  const result: Record<string, boolean> = {};

  if (!daysArray || !Array.isArray(daysArray)) {
    return result;
  }

  // Create object with all days set to false by default
  for (let i = 0; i < 7; i++) {
    result[i] = false;
  }

  // Set selected days to true
  daysArray.forEach(day => {
    if (day >= 0 && day <= 6) {
      result[day] = true;
    }
  });

  return result;
}
